import { Injectable } from '@angular/core';
import { appSettings } from '@nha/config';
import { Environments } from '@nha/enums/environments.enum';
import { getAppDomain, newGuid } from '@nha/functions';
import { StorageService } from '@nha/services';
import * as Cookies from 'es-cookie';
import { BehaviorSubject } from 'rxjs';
import { SessionKeys } from '../enums/session-keys.enums';
import * as JWT from '../functions/jwt-helper.functions';
import { IEntitlement, IUser, IUserSession, User, UserSession } from '../types';
import { ReAuthenticationRequiredError } from '../types/re-authentication-required.error';
import { UserProfileLoadError } from '../types/user-profile-load-error.type';

/** The API's exception middleware returns `{ type, message, ... }`; anything else falls back to the status line. */
function readApiError(request: XMLHttpRequest): { message: string; type?: string } {
	try {
		const body = JSON.parse(request.responseText);
		if (typeof body?.message === 'string' && body.message.length > 0) {
			return { message: body.message, type: typeof body.type === 'string' ? body.type : undefined };
		}
	} catch {
		// not JSON; fall through
	}

	return { message: `HTTP ${request.status}${request.statusText ? ` ${request.statusText}` : ''}` };
}

@Injectable()
export class UserSessionService {
	private _userSession: IUserSession = null;
	private _tokenCheckTimer: number = null;
	private _oktaRefreshTimer: number = null;
	private readonly _storageService: StorageService = new StorageService();

	public tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	constructor() {
		this._userSession = this.getUserSession();
	}

	getUserSession(): IUserSession {
		const userSession: IUserSession = new UserSession();
		const token: string = this.getTokenFromCookie();

		if (token === undefined) {
			return userSession;
		}

		this.tokenSubject.next(token);

		if (appSettings.authentication.clientID === JWT.getTokenValue(token, 'aud')) {
			userSession.authenticated = true;
			userSession.token = token;
			userSession.expires = JWT.getTokenExpirationDate(token);
			userSession.user = this.getUserProfile();

			return userSession;
		}

		this.logout();
	}

	/**
	 * Rejects (with a UserProfileLoadError for the profile call, a plain Error for a failed silent
	 * sign-in) instead of swallowing the failure, so an app can show something rather than leaving
	 * the user on the loader.
	 */
	init(): Promise<void> {
		return this.refreshToken()
			.then(() => this.loadUserProfile())
			.then(() => {
				// getUserSession() re-emits the token on tokenSubject itself.
				this._userSession = this.getUserSession();
			});
	}

	isAuthenticated(): boolean {
		if (appSettings.authentication.isDisabled) {
			return true;
		}

		if (!this._userSession.authenticated) {
			return false;
		}

		this.watchTokenDelete();
		this.setOktaRefreshTimer();

		return true;
	}

	login(): void {
		/** check if the user is authenticated */
		if (this._userSession.authenticated) {
			return;
		}

		/** check for the code in the url */
		const hash: string = window.location.hash;
		if (hash !== null && hash !== '') {
			/** get the hash url params */
			const urlParams: URLSearchParams = new URLSearchParams(window.location.hash.substring(1));

			/** check for the jwt token */
			if (urlParams.has('id_token')) {
				/** get the token */
				const token: string = urlParams.get('id_token');

				/** set the token in the cookie */
				if (!JWT.isTokenExpired(token)) {
					/** set the cookie */
					Cookies.set(SessionKeys.SESSION_USER_TOKEN, token, { path: '/', secure: true, domain: getAppDomain() });

					/** get all required information */
					if (!this.isIframe() || this.isLegacy()) {
						// The cookie is set and the destination app runs init() itself (and owns showing any
						// error), so waiting on a profile fetch here only slows the sign-in down.
						this.redirect(urlParams.get('state') ?? this.getDevRedirectUrl());
					}
					return;
				}

				/** return */
				return;
			}

			/** check for errors */
			if (urlParams.has('error')) {
				/** create error message and display */
				console.error(`Authentication Error: ${urlParams.get('error')}.\r\nError Description: ${urlParams.get('error_description')}`);
				window.alert(`Your session has either expired or an error occurred. Please login again.`);

				if (this.isIframe()) {
					this.clearSession();
					this.clearTokenRefreshing();
					window.parent.location.reload();
					return;
				}

				this.logout();
			}
		} else {
			/** direct to sign in page */
			this.redirect(this.getSignInUrl(false));
		}
	}

	/**
	 * Clears the local session and sends the user through the interactive sign-in, returning to the
	 * current URL. Unlike logout(), the Okta session is kept, so a live session re-issues the token
	 * without a credential prompt.
	 */
	reAuthenticate(): void {
		const signInUrl: string = this.getSignInUrl(false);
		this.clearSession();
		this.redirect(signInUrl);
	}

	logout(): void {
		const signOutUrl: string = this.getSignOutUrl();
		this.clearSession();
		this.redirect(signOutUrl);
	}

	hasUserToken(): boolean {
		/** check for the token in the cookie */
		const token: string = Cookies.get(SessionKeys.SESSION_USER_TOKEN);
		if (token !== undefined) {
			return true;
		}
		return false;
	}

	getTokenFromCookie(): string {
		return Cookies.get(SessionKeys.SESSION_USER_TOKEN);
	}

	getUser(): IUser {
		return this._userSession !== null ? this._userSession.user : new User();
	}

	refreshToken(override: boolean = false): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			/** get the current token; a missing cookie must fall through to the silent refresh, not throw */
			if (!override) {
				const token: string = this.getTokenFromCookie();
				if (token && !JWT.isTokenExpired(token)) {
					resolve(token);
					return;
				}
			}

			/** set the token refreshing flag */
			this.setTokenRefreshing();

			/** create the signin url */
			const signInUrl: string = this.getSignInUrl(true);

			/** snapshot the profile: clearSession wipes it, and a failed reload should fall back to it */
			const previousProfile: IUser = this._storageService.hasKey(SessionKeys.USER_PROFILE)
				? this._storageService.get<IUser>(SessionKeys.USER_PROFILE)
				: null;

			/** clear the session */
			this.clearSession();

			/** create hidden iframe */
			let iframe: HTMLIFrameElement = document.createElement('iframe');
			document.body.appendChild(iframe);
			iframe.style.display = 'none';
			iframe.setAttribute('src', signInUrl);

			const finish = (): void => {
				this.clearTokenRefreshing();
				document.body.removeChild(iframe);
				iframe = null;
			};

			iframe.onerror = () => {
				reject(new Error('The silent sign-in frame failed to load.'));
				finish();
			};

			iframe.onload = () => {
				const refreshedToken: string = this.getTokenFromCookie();

				// No cookie back from the silent sign-in means the Okta session is gone: only the
				// interactive sign-in can recover, so tell the caller that rather than "an error".
				if (!refreshedToken) {
					reject(new ReAuthenticationRequiredError('Silent token refresh did not return a session token.'));
					finish();
					return;
				}

				// The token is valid even if the profile reload fails, so in-flight requests waiting on
				// this refresh must not be stranded: restore the pre-refresh profile (clearSession wiped
				// it) and continue; the next successful load replaces it.
				this.loadUserProfile()
					.catch((error) => {
						console.error('Profile reload after token refresh failed.', error);
						if (previousProfile) {
							this._storageService.set(SessionKeys.USER_PROFILE, previousProfile);
						}
					})
					.then(() => {
						this._userSession = this.getUserSession();
						this.watchTokenDelete();
						console.log(`Token Refreshed: ${new Date().toLocaleTimeString()}`);
						resolve(refreshedToken);
					})
					.finally(finish);
			};
		});
	}

	killOktaSession(): Promise<void> {
		return new Promise<void>((resolve) => {
			let iframe: HTMLIFrameElement = document.createElement('iframe');
			document.body.appendChild(iframe);
			iframe.style.display = 'none';
			iframe.setAttribute('src', this.getSignOutUrl());
			iframe.onload = () => {
				console.log(`Okta Session Killed: ${new Date().toLocaleTimeString()}`);
				resolve();

				document.body.removeChild(iframe);
				iframe = null;
			};
		});
	}

	isIframe(): boolean {
		return window !== window.parent;
	}

	isLegacy(): boolean {
		if (window.parent === undefined) {
			// we are not in an iframe, so cannot be legacy
			return false;
		}

		const url = new URL(document.location.href).searchParams;
		return url.get('fromLegacy') === 'true';
	}

	hasEntitlement(entitlement: string): boolean;
	hasEntitlement(entitlementType: string, entitlementName: string): boolean;
	hasEntitlement(entitlement: { entitlementType: string; entitlementName: string }): boolean;
	hasEntitlement(x: string | { entitlementType: string; entitlementName: string }, y?: string): boolean {
		/** default the values */
		let entitlementType: string = null;
		let entitlementName: string = null;

		/** check the type of x */
		if (typeof x === 'string') {
			/** check for pipe */
			if (x.includes('|')) {
				const entitlementParts: string[] = x.split('|');
				entitlementType = entitlementParts[0];
				entitlementName = entitlementParts[1];
			} else {
				entitlementType = x;
				entitlementName = y;
			}
		} else {
			entitlementType = x.entitlementType;
			entitlementName = x.entitlementName;
		}

		/** run the check */
		const entitlements: IEntitlement[] = this._userSession?.user?.entitlements;
		if (!entitlements) {
			return false;
		}

		return entitlements.any((entitlement: IEntitlement) => {
			return entitlement.entitlementType === entitlementType && entitlement.entitlementName === entitlementName;
		});
	}

	hasAnyEntitlement(entitlements: string[]): boolean;
	hasAnyEntitlement(entitlements: { entitlementType: string; entitlementName: string }[]): boolean;
	hasAnyEntitlement(x: string[] | { entitlementType: string; entitlementName: string }[]): boolean {
		let hasEntitlement: boolean[] = [];
		if (typeof x[0] === 'string') {
			const entitlements: string[] = x as string[];
			hasEntitlement = entitlements.map((fp) => this.hasEntitlement(fp));
		} else {
			const entitlements: { entitlementType: string; entitlementName: string }[] = x as { entitlementType: string; entitlementName: string }[];
			hasEntitlement = entitlements.map((fp) => this.hasEntitlement(fp));
		}

		return hasEntitlement.includes(true);
	}

	hasAllEntitlements(entitlements: string[]): boolean;
	hasAllEntitlements(entitlements: { entitlementType: string; entitlementName: string }[]): boolean;
	hasAllEntitlements(x: string[] | { entitlementType: string; entitlementName: string }[]): boolean {
		let hasEntitlement: boolean[] = [];
		if (typeof x[0] === 'string') {
			const entitlements: string[] = x as string[];
			hasEntitlement = entitlements.map((fp) => this.hasEntitlement(fp));
		} else {
			const entitlements: { entitlementType: string; entitlementName: string }[] = x as { entitlementType: string; entitlementName: string }[];
			hasEntitlement = entitlements.map((fp) => this.hasEntitlement(fp));
		}

		return hasEntitlement.every((val: boolean) => val === true);
	}

	/** private methods */

	private getSignInUrl(refresh: boolean = false, state: string = window.location.href): string {
		let signInUrl: string = `${appSettings.authentication.authenticationServer}${
			appSettings.authentication.signInEndpoint
		}?response_type=id_token&scope=openid+profile+email&client_id=${
			appSettings.authentication.clientID
		}&redirect_uri=${this.getDevRedirectUrl()}&state=${state}&nonce=${newGuid()}`;

		if (refresh === true) {
			signInUrl = `${signInUrl}&prompt=none`;
		}

		return signInUrl;
	}

	private getSignOutUrl(): string {
		let signOutUrl: string = `${appSettings.authentication.authenticationServer}${
			appSettings.authentication.signOutEndpoint
		}?post_logout_redirect_uri=${this.getDevRedirectUrl()}`;

		if (this.hasUserToken()) {
			const token: string = this.getTokenFromCookie();
			signOutUrl = `${signOutUrl}&id_token_hint=${token}`;
		}

		return signOutUrl;
	}

	private getDevRedirectUrl(): string {
		if (appSettings.env === Environments.DEV) {
			const temp = window.location.href.match('://(.[^/]+)(.*)');
			return `https://${temp[1]}/`;
		} else {
			return appSettings.authentication.redirectUri;
		}
	}

	private isTokenRefreshing(): boolean {
		return !!Cookies.get(SessionKeys.SESSION_IS_REFRESHING);
	}

	private setTokenRefreshing(): void {
		Cookies.set(SessionKeys.SESSION_IS_REFRESHING, 'true', { path: '/', secure: true, domain: getAppDomain() });
	}

	private clearTokenRefreshing(): void {
		Cookies.remove(SessionKeys.SESSION_IS_REFRESHING, { path: '/', secure: true, domain: getAppDomain() });
	}

	private clearSession(): void {
		/** kill the token timers */
		window.clearInterval(this._tokenCheckTimer);
		this._tokenCheckTimer = null;

		/** clear the user session */
		this._userSession = new UserSession();

		/** clear storage */
		this._storageService.clear();

		/** send null token */
		this.tokenSubject.next(null);

		/** clear the various storage */
		const domain: string = getAppDomain();
		Cookies.remove(SessionKeys.SESSION_USER_TOKEN, { path: '/', secure: true, domain: domain });
		Cookies.remove(SessionKeys.IMPERSONATION_COOKIE, { path: '/', secure: true, domain: domain });
	}

	private watchTokenDelete(): void {
		window.clearInterval(this._tokenCheckTimer);
		this._tokenCheckTimer = window.setInterval(() => {
			const userToken: string = this.getTokenFromCookie();
			if (userToken === undefined && !this.isTokenRefreshing()) {
				this.redirect(appSettings.authentication.redirectUri);
			}
		}, 1000);
	}

	private setOktaRefreshTimer(): void {
		window.clearInterval(this._oktaRefreshTimer);
		this._oktaRefreshTimer = window.setInterval(() => {
			this.refreshOktaSession();
		}, 900000); // 15 minutes
	}

	private redirect(url: string): void {
		window.location.href = url;
	}

	private getUserProfile(): IUser {
		/** check sesson storage */
		if (this._storageService.hasKey(SessionKeys.USER_PROFILE)) {
			const profile: IUser = this._storageService.get<IUser>(SessionKeys.USER_PROFILE);

			/** a poisoned cache (e.g. an error body stored as the profile) must read as "no profile" */
			if (profile && Array.isArray(profile.entitlements)) {
				return profile;
			}
		}

		return new User();
	}

	private loadUserProfile(): Promise<IUser> {
		return new Promise<IUser>((resolve, reject) => {
			/** get the token */
			const token: string = this.getTokenFromCookie();

			/** create the request */
			const userProfileRequest: XMLHttpRequest = new XMLHttpRequest();
			userProfileRequest.open('GET', `${appSettings.api.account}user`);
			userProfileRequest.setRequestHeader('Authorization', `Bearer ${token}`);

			if (Cookies.get(SessionKeys.IMPERSONATION_COOKIE)) {
				userProfileRequest.setRequestHeader('impersonation-user', Cookies.get(SessionKeys.IMPERSONATION_COOKIE));
				userProfileRequest.setRequestHeader('impersonation-user-type', 'staff');
				userProfileRequest.setRequestHeader('impersonation-application', 'mynha');
			}

			userProfileRequest.onload = () => {
				// A failed call must never be cached as the profile: an error body has no entitlements,
				// and every app reads the cached profile on its next load.
				if (userProfileRequest.status < 200 || userProfileRequest.status >= 300) {
					const apiError = readApiError(userProfileRequest);
					reject(new UserProfileLoadError(userProfileRequest.status, apiError.message, apiError.type));
					return;
				}

				let user: IUser;
				try {
					user = JSON.parse(userProfileRequest.responseText);
				} catch {
					reject(new UserProfileLoadError(userProfileRequest.status, 'Unreadable user profile response'));
					return;
				}

				// A 200 whose body is not a profile (null, an error envelope) must fail like an error
				// status; caching it would boot the app as an entitlement-less anonymous user.
				if (!user || !Array.isArray(user.entitlements)) {
					reject(new UserProfileLoadError(userProfileRequest.status, 'Unexpected user profile response shape'));
					return;
				}

				this._storageService.set(SessionKeys.USER_PROFILE, user);
				resolve(user);
			};
			userProfileRequest.onerror = () => {
				reject(new UserProfileLoadError(0, 'Network error while retrieving the user profile'));
			};

			/** send the request */
			userProfileRequest.send();
		});
	}

	private refreshOktaSession(): void {
		const refreshRequest: XMLHttpRequest = new XMLHttpRequest();
		refreshRequest.open('GET', `${appSettings.authentication.authenticationServer}/api/v1/sessions/me`);
		refreshRequest.withCredentials = true;
		refreshRequest.onload = () => {
			console.log(`Okta Session Lifetime Refreshed: ${new Date().toLocaleTimeString()}`);
			console.log(JSON.parse(refreshRequest.responseText));
		};
		refreshRequest.onerror = () => console.log('Could not refresh Okta session');
		refreshRequest.send();
	}
}
